import { getCurrentUser } from '@lastbrand/fe-common/universal-auth'

import { mapSvdProduct } from '@src/components/svd-product-card/svd-card.mapper'
import { MODULES, FEATURES } from '@src/quince-site-common/src/constants/template-info'
import wishListStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import productService from '@src/quince-site-common/src/services/productService'
import { mergeSvdWishlistOnLogin } from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.login-merge'
import svdWishlistService from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.service'
import { backfillSvdWishlist } from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.store'
import { IWishlistItem } from '@src/quince-site-common/src/services/Wishlist.types'
import wishlistService from '@src/quince-site-common/src/services/wishlistService'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import type { ISvdCard, SvdCardToken } from '@src/types/svd-product-card.types'
import { createBatches } from '@src/utils/general.utils'

import type { SvdWishlistCard, SvdWishlistPageData } from './svd-wishlist.types'

// v2 returns the wishlisted variant first (+ echoes its selectedAttributes), so pre-select it directly.
function toSvdWishlistCard(card: ISvdCard): SvdWishlistCard {
  const product = mapSvdProduct(card)
  return { product, selectedVariantId: product.cardVariants[0]?.variantId }
}

/**
 * SVD self-wishlist hydration (Phase 2), branched by auth:
 * - Logged-in: merge any local guest items into `/v2` (additive), then render the authoritative
 *   `/v2/get-active-wishlist-products` cards (already hydrated as `ISvdCard`) and sync the local cache.
 * - Guest: lazily backfill spid→productId, then hydrate the productId+selectedAttributes superset via
 *   `/products/cards`.
 *
 * Auth is resolved via the async `getCurrentUser()` (restores the session on a cold refresh) — NOT the
 * synchronous `AuthUtil.isLoggedIn()`, which returns false on refresh before the session rehydrates
 * (empty in-memory store + empty `gatsbyUser` fallback) and would wrongly send a logged-in user down
 * the guest path (`/products/cards` instead of `/v2/get-active-wishlist-products`).
 *
 * Long-tail safety: storage is never destructively rewritten; unresolved spids and dropped cards are
 * simply skipped this load and retried next visit — never lost.
 */
export const fetchSvdWishlistProducts = async (): Promise<SvdWishlistCard[]> => {
  try {
    const currentUser = await getCurrentUser()
    return currentUser?.email ? await fetchSvdWishlistLoggedIn() : await fetchSvdWishlistGuest()
  } catch (error) {
    captureException(error, {
      module: MODULES.WISHLIST,
      feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
    })
    return []
  }
}

async function fetchSvdWishlistLoggedIn(): Promise<SvdWishlistCard[]> {
  // Same merge that runs on the login event (push local guest items to v2, read back, sync the
  // local cache); reuse its authoritative cards to render.
  const cards = await mergeSvdWishlistOnLogin()
  return cards.map(toSvdWishlistCard)
}

async function fetchSvdWishlistGuest(): Promise<SvdWishlistCard[]> {
  await backfillSvdWishlist()
  const wishlistItems = wishlistService.fetchUserWishListFromLocalStorage()
  wishListStateManager.setWishlistTotalItemsCount(wishlistItems.length)
  if (wishlistItems.length === 0) return []

  // One token per (productId, selectedAttributes) pair — keep the source entry so we pair the
  // (drop-on-miss, request-ordered) response back by identity, never by position.
  const tokens: SvdCardToken[] = []
  const tokenItems: IWishlistItem[] = []
  for (const item of wishlistItems) {
    if (!item.productId) continue // unresolved spid (e.g. retired) — kept stored, skipped this load
    tokens.push({ productId: item.productId, selectedAttributes: item.selectedAttributes ?? { color: item.color } })
    tokenItems.push(item)
  }
  if (tokens.length === 0) return []

  // Batched, not one request for the whole wishlist (STRFRNT-15810 follow-up): an unbatched call sent
  // every wishlisted item's token in a single POST body, so one slow/large wishlist could turn into a
  // slow or oversized request with no partial-failure isolation — a bad response dropped the whole
  // wishlist instead of just the entries in it. `createBatches`'s default size matches the other
  // client of this endpoint (`svd-cards-refresh.store.ts`); `allSettled` means one bad batch degrades
  // only that batch's entries (reported, same as `getProductCardsBatched`), not the whole wishlist.
  const batches = createBatches(tokens)
  const results = await Promise.allSettled(batches.map(batch => productService.getSvdProductCards(batch)))
  const svdCards = results.flatMap((result, index) => {
    if (result.status === 'fulfilled') return result.value
    captureException(result.reason, {
      module: MODULES.WISHLIST,
      feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
      extraInfo: { batchIndex: index, batchSize: batches[index]?.length },
    })
    return []
  })
  return reconcileSvdWishlistCards(svdCards, tokens, tokenItems)
}

/** SVD shared-wishlist hydration — `/v2/get-shared-wishlist` returns hydrated cards (no auth). */
export const fetchSvdSharedWishlist = async (token: string): Promise<SvdWishlistPageData> => {
  try {
    const { userName, cards } = await svdWishlistService.getSharedWishlist(token)
    return { name: userName, cards: cards.map(toSvdWishlistCard) }
  } catch (error) {
    captureException(error, {
      module: MODULES.WISHLIST,
      feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
    })
    return { cards: [] }
  }
}

/** Unified SVD wishlist fetch for the page: shared when a token is present, else the self wishlist. */
export const fetchSvdWishlist = async (id: string | null): Promise<SvdWishlistPageData> => {
  if (id) return fetchSvdSharedWishlist(id)
  return { cards: await fetchSvdWishlistProducts() }
}

// Pair the `/products/cards` response (request order, dropped cards absent) back to the wishlist
// entries by identity (productId + anchor value), case-insensitively — never positionally. For each
// matched card, pre-select the variant whose anchor value the user wishlisted so the saved colour
// shows first.
//
// A single per-productId queue is the sole source of truth: each token find-and-splices its card, so
// a card is consumed by at most one token. This prevents cross-assignment when the same productId is
// wishlisted in multiple colours and SAND's FALLBACK swatch mode makes two cards echo the same colour
// (a dual exact-match-map + fallback-queue design would double-consume a card there).
function reconcileSvdWishlistCards(
  svdCards: ISvdCard[],
  tokens: SvdCardToken[],
  tokenItems: IWishlistItem[],
): SvdWishlistCard[] {
  const norm = (value: string) => value.trim().toLowerCase()
  const echoedColorOf = (card: ISvdCard): string | undefined =>
    card.cardVariants[0]?.selectedAttributes?.color ?? card.cardVariants[0]?.displayConfig?.value

  const byProductId = new Map<string, ISvdCard[]>()
  for (const card of svdCards) {
    const productId = String(card.productId)
    const list = byProductId.get(productId) ?? []
    list.push(card)
    byProductId.set(productId, list)
  }

  const result: SvdWishlistCard[] = []
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    const item = tokenItems[i]
    if (!token || !item) continue
    const queue = byProductId.get(token.productId)
    if (!queue || queue.length === 0) continue // dropped by the endpoint — keep stored, skip this load

    // Prefer the first remaining card whose echoed colour matches; else the first remaining card
    // (echo mismatch / FALLBACK swatch). Splice so each card is claimed by exactly one token.
    const requestedColor = item.color
    let index = queue.findIndex(card => {
      const echoed = echoedColorOf(card)
      return echoed !== undefined && norm(echoed) === norm(requestedColor)
    })
    if (index === -1) index = 0
    const card = queue.splice(index, 1)[0]
    if (!card) continue

    const product = mapSvdProduct(card)
    const selectedVariant = product.cardVariants.find(
      variant => variant.displayConfig && norm(variant.displayConfig.value) === norm(requestedColor),
    )
    result.push({ product, selectedVariantId: selectedVariant?.variantId })
  }
  return result
}
